import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

import { getBranch } from '../../store/user/selectors';
import { getQboSession } from '../../store/settings/selectors';
import { URLS } from 'resources/constants';
import { thirdPartyApi } from '../../resources/hooks/api/thirdPartyApiHook';
import { useSettingsApi } from '../../resources/hooks/api/useSettingsApiHook';

const ThirdPartyConnection: React.FC = () => {
    const navigate = useNavigate();
    const { quickBookAuth } = thirdPartyApi();
    const branchData = useSelector(getBranch);
    const quickBookSession = useSelector(getQboSession);
    const { fetchAccountSettingsData } = useSettingsApi();
    const { t } = useTranslation("scenes.thirdparty.connection");

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [selectedItem] = useState<string>('quick-book');
    const newWindow = useRef<Window | null>(null);
    const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);

    const handleLogin = () => {
        if (selectedItem === 'quick-book') {
            _handleQuickBookAuth();
        } else {
            toast.error(t("saga.not.available"));
        }
    };

    const _handleQuickBookAuth = () => {
        setIsLoading(true);
        quickBookAuth({ branch_id: +branchData['id'] }, (_message: string, resp: any) => {
            const authUrl = resp.data.data.auth_url;
            window.location.href = authUrl;
        }, () => {
            setIsLoading(false);
        });
    };

    useEffect(() => {
        if (!quickBookSession) {
            navigate(URLS.QUICKBOOK_DASHBOARD);
        }
    }, [quickBookSession, navigate]);

    return (
        <div className="main-container flex-grow-1 bg-light">
            <div className="container-fluid py-4" style={{ maxWidth: '1100px', margin: '0 auto' }}>
                <div className="d-flex align-items-center justify-content-between mb-4">
                    <div>
                        <h1 className="h4 font-weight-700 mb-1 text-dark">{t('page.title')}</h1>
                        <p className="text-muted small mb-0">
                            {t('page.subtitle')}
                        </p>
                    </div>
                    <button type="button" className="btn btn-outline-primary" onClick={() => navigate(URLS.DASHBOARD)}>
                        {t('scenes.dashboard:dashboard.setup_method.back')}
                    </button>
                </div>

                <div className="card shadow-sm border-0 rounded-4 overflow-hidden">
                    <div className="card-body p-4 p-md-5">
                        <div className={`page-loader ${isLoading ? 'visible' : ''}`}>
                            <div className="loader"></div>
                        </div>

                        <div className="mb-4">
                            <span className="badge bg-primary-subtle text-primary font-weight-700 px-3 py-1.5 rounded-pill mb-3" style={{ fontSize: '12px' }}>
                                {t('integration.badge')}
                            </span>
                            <h4 className="font-weight-700 text-dark mb-2">QuickBooks Online (QBO)</h4>
                            <p className="text-muted" style={{ maxWidth: '650px', lineHeight: '1.6' }}>
                                {t('integration.description')}
                            </p>
                        </div>

                        {/* Integration Card */}
                        <div className="p-4 rounded-3 border border-2 border-primary-subtle bg-primary-subtle bg-opacity-10 d-flex flex-wrap align-items-center justify-content-between gap-4 mb-5">
                            <div className="d-flex align-items-center gap-3">
                                <div className="bg-white p-3 rounded-3 shadow-sm border d-flex align-items-center justify-content-center" style={{ width: '80px', height: '60px' }}>
                                    <img src="quick-book.svg" alt="QuickBooks Online" style={{ height: '36px', objectFit: 'contain' }} />
                                </div>
                                <div>
                                    <h5 className="font-weight-700 mb-1 text-dark">{t('integration.appName')}</h5>
                                    <div className="d-flex align-items-center gap-2">
                                        <span className="badge bg-success-subtle text-success border border-success-subtle rounded-pill">
                                            <i className="fas fa-check-circle me-1"></i> {t('integration.oauthReady')}
                                        </span>
                                        <span className="text-muted small">• {t('integration.completeSync')}</span>
                                    </div>
                                </div>
                            </div>

                            <button
                                type="button"
                                className="btn btn-primary px-4 py-2.5 rounded-pill font-weight-600 shadow-sm d-inline-flex align-items-center gap-2"
                                onClick={handleLogin}
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm" role="status"></span>
                                        <span>{t('button.connecting')}</span>
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-plug"></i>
                                        <span>{t('button.connectNow')}</span>
                                    </>
                                )}
                            </button>
                        </div>

                        {/* Security notice */}
                        <div className="d-flex align-items-center gap-3 p-3 bg-light rounded-3 text-muted small border">
                            <i className="fas fa-shield-alt text-primary fa-lg me-1"></i>
                            <span>
                                {t('security.note')}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ThirdPartyConnection;
